// Manga Statistics tab component — adapted from MyStats.js for chapters/volumes

import React, { useMemo, useState, useEffect } from 'react';
import { useMangaSource } from '../context/MangaSourceContext';
import {
    calculateMangaTabStats,
    formatChapterProgress
} from '../utils/timeFormatter';
import { filterMangaGroupsByStatus } from '../services/groupingService';
import { StatsSkeleton } from './common/SkeletonLoader';
import ErrorMessage from './common/ErrorMessage';
import './MyStats.css';

// Tab configuration matching the manga Overview tabs
const TAB_CONFIG = [
    { id: 'ALL', label: 'All', emoji: '📋', colorVar: '--stats-color-all' },
    { id: 'CURRENT', label: 'Reading', emoji: '📖', colorVar: '--stats-color-watching' },
    { id: 'CONTINUE', label: 'Continue', emoji: '▶️', colorVar: '--stats-color-continue' },
    { id: 'PLANNING', label: 'Planning', emoji: '📝', colorVar: '--stats-color-planning' },
    { id: 'PAUSED', label: 'On Hold', emoji: '⏸️', colorVar: '--stats-color-onhold' },
    { id: 'DROPPED', label: 'Dropped', emoji: '💔', colorVar: '--stats-color-dropped' },
    { id: 'HALF_COMPLETED', label: 'Half Completed', emoji: '½', colorVar: '--stats-color-halfcompleted' },
    { id: 'COMPLETED', label: 'Completed', emoji: '✅', colorVar: '--stats-color-completed' }
];

const MangaStats = () => {
    const { mangaUserData, isLoading, mangaGroups, refreshData } = useMangaSource();
    const [activeInfo, setActiveInfo] = useState(null);

    const toggleInfo = (e, id) => {
        e.stopPropagation();
        setActiveInfo(prev => prev === id ? null : id);
    };

    useEffect(() => {
        if (!activeInfo) return;
        const handleClickOutside = () => setActiveInfo(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [activeInfo]);

    // Calculate per-tab stats from group-level data
    const tabStats = useMemo(() => {
        if (!mangaGroups || mangaGroups.length === 0) return null;

        const result = {};
        TAB_CONFIG.forEach(tab => {
            // filterMangaGroupsByStatus takes (groups, status), so wrap it to pass the tab id
            const filtered = filterMangaGroupsByStatus(mangaGroups, tab.id);
            result[tab.id] = calculateMangaTabStats(filtered, tab.id);
        });
        return result;
    }, [mangaGroups]);

    const handleRefresh = () => {
        if (refreshData) refreshData(true);
    };

    if (!mangaUserData) {
        return (
            <div className="statistics-container">
                <div className="empty-state">
                    <div className="empty-icon">📊</div>
                    <h2>No Manga Statistics Yet</h2>
                    <p>Connect your AniList account in Settings to see your manga stats!</p>
                </div>
            </div>
        );
    }

    if (isLoading && !tabStats) {
        return <StatsSkeleton />;
    }

    const allStats = tabStats?.['ALL'] || {};

    const mangaReadCount = (tabStats?.['CURRENT']?.groupCount || 0)
        + (tabStats?.['CONTINUE']?.groupCount || 0)
        + (tabStats?.['PAUSED']?.groupCount || 0)
        + (tabStats?.['DROPPED']?.groupCount || 0)
        + (tabStats?.['HALF_COMPLETED']?.groupCount || 0)
        + (tabStats?.['COMPLETED']?.groupCount || 0);

    const mangaPlannedCount = tabStats?.['PLANNING']?.groupCount || 0;

    return (
        <div className="statistics-container">
            {/* Header */}
            <div className="stats-header">
                <h1 className="stats-title">📊 Manga Stats</h1>
            </div>

            {/* Overall Summary */}
            <div className="stats-overall-summary">
                <div className="summary-card-new total-anime">
                    <div className="summary-icon">📖</div>
                    <div className="summary-data">
                        <div className="summary-value-new">
                            {allStats.animeCount || allStats.mangaCount || 0}
                            <span className="info-wrapper">
                                <button className={`info-btn ${activeInfo === 'totalManga' ? 'active' : ''}`} onClick={(e) => toggleInfo(e, 'totalManga')}>i</button>
                                {activeInfo === 'totalManga' && <span className="info-popover">Number of manga you have in AniList</span>}
                            </span>
                        </div>
                        <div className="summary-label-new">Total Manga</div>
                    </div>
                </div>
                <div className="summary-card-new total-groups">
                    <div className="summary-icon">📚</div>
                    <div className="summary-data">
                        <div className="summary-value-new">
                            {allStats.groupCount || 0}
                            <span className="info-wrapper">
                                <button className={`info-btn ${activeInfo === 'totalGroups' ? 'active' : ''}`} onClick={(e) => toggleInfo(e, 'totalGroups')}>i</button>
                                {activeInfo === 'totalGroups' && <span className="info-popover">Number of unique manga series</span>}
                            </span>
                        </div>
                        <div className="summary-label-new">Total Groups</div>
                    </div>
                </div>
                <div className="summary-card-new total-episodes">
                    <div className="summary-icon">📖</div>
                    <div className="summary-data">
                        <div className="summary-value-new">
                            {formatChapterProgress(allStats.readChapters || allStats.watchedEpisodes, allStats.totalChapters || allStats.totalEpisodes)}
                            <span className="info-wrapper">
                                <button className={`info-btn ${activeInfo === 'totalChapters' ? 'active' : ''}`} onClick={(e) => toggleInfo(e, 'totalChapters')}>i</button>
                                {activeInfo === 'totalChapters' && <span className="info-popover">Chapters read out of total chapters across your entire manga collection</span>}
                            </span>
                        </div>
                        <div className="summary-label-new">Chapters</div>
                    </div>
                </div>
                {mangaReadCount > 0 && (
                    <div className="summary-card-new series-watched">
                        <div className="summary-icon">👁️</div>
                        <div className="summary-data">
                            <div className="summary-value-new">
                                {mangaReadCount}
                                <span className="info-wrapper">
                                    <button className={`info-btn ${activeInfo === 'mangaRead' ? 'active' : ''}`} onClick={(e) => toggleInfo(e, 'mangaRead')}>i</button>
                                    {activeInfo === 'mangaRead' && <span className="info-popover">Number of unique manga read</span>}
                                </span>
                            </div>
                            <div className="summary-label-new">Different Manga Read</div>
                        </div>
                    </div>
                )}
                {mangaPlannedCount > 0 && (
                    <div className="summary-card-new animes-planned">
                        <div className="summary-icon">📝</div>
                        <div className="summary-data">
                            <div className="summary-value-new">
                                {mangaPlannedCount}
                                <span className="info-wrapper">
                                    <button className={`info-btn ${activeInfo === 'mangaPlanned' ? 'active' : ''}`} onClick={(e) => toggleInfo(e, 'mangaPlanned')}>i</button>
                                    {activeInfo === 'mangaPlanned' && <span className="info-popover">Number of unique manga you plan to read</span>}
                                </span>
                            </div>
                            <div className="summary-label-new">New Manga Planned</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Per-Tab Stats */}
            <div className="stats-tabs-section">
                <h2 className="section-title-new">📈 Stats by Category</h2>
                <div className="stats-tab-list">
                    {TAB_CONFIG.map(tab => {
                        const s = tabStats?.[tab.id];
                        if (!s) return null;
                        if (tab.id !== 'ALL' && s.groupCount === 0) return null;

                        return (
                            <div
                                key={tab.id}
                                className={`stats-tab-row tab-${tab.id.toLowerCase()}`}
                                style={{ '--tab-accent': `var(${tab.colorVar})` }}
                            >
                                <div className="tab-identity">
                                    <span className="tab-emoji">{tab.emoji}</span>
                                    <div className="tab-name-group">
                                        <span className="tab-name">{tab.label}</span>
                                        <span className="tab-meta">
                                            {s.groupCount} group{s.groupCount !== 1 ? 's' : ''} · {s.animeCount || s.mangaCount || 0} manga
                                        </span>
                                    </div>
                                </div>

                                {/* Three Columns — Chapters instead of Time */}
                                <div className="tab-time-grid">
                                    <div className="time-cell total">
                                        <span className="time-cell-label">📖 Total</span>
                                        <span className="time-cell-value">{s.totalChapters || s.totalEpisodes || '?'} ch</span>
                                    </div>
                                    <div className="time-cell watched">
                                        <span className="time-cell-label">✅ Read</span>
                                        <span className="time-cell-value">{s.readChapters || s.watchedEpisodes || 0} ch</span>
                                    </div>
                                    <div className="time-cell remaining">
                                        <span className="time-cell-label">⏳ Remaining</span>
                                        <span className="time-cell-value">{s.remainingChapters || s.remainingEpisodes || 0} ch</span>
                                    </div>
                                </div>

                                {/* Volumes & Score */}
                                <div className="tab-extras">
                                    {(s.totalVolumes || 0) > 0 && (
                                        <span className="extras-item">
                                            📕 {s.readVolumes || 0}/{s.totalVolumes} vol
                                        </span>
                                    )}
                                    {s.averageScore > 0 && (
                                        <span className="extras-item">
                                            ⭐ {(s.averageScore / 10).toFixed(1)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default MangaStats;
